import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { hrApi } from '@/api';
import { EmployeeSelect, LeaveTypeSelect } from '@/components/selects';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { FormGrid } from '@/components/ui/FormGrid';
import { FormActions } from '@/components/ui/FormActions';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { ViewModeToggle, type ViewMode } from '@/components/ui/ViewModeToggle';
import { HrCalendarGranularityToggle } from '@/modules/hr/components/HrCalendarGranularityToggle';
import {
  HrEmployeeScheduleGrid,
  hrEmployeeInitials,
  type HrScheduleEmployee,
} from '@/modules/hr/components/HrEmployeeScheduleGrid';
import {
  HR_LEAVE_STYLES,
  hrDateKey,
  hrFormatDaysCount,
  hrFormatLeaveDays,
  hrLeaveSpansDate,
  hrCalendarDaysForGranularity,
  hrCalendarRangeForGranularity,
  type HrCalendarGranularity,
} from '@/modules/hr/components/hrCalendarUtils';
import { HrWorkforceFilterBar, type HrWorkforceFilters } from '@/modules/hr/components/HrWorkforceFilterBar';
import { fetchAllHrPages } from '@/modules/hr/components/hrCalendarFetch';
import { HrScheduleCalendarLegend } from '@/modules/hr/components/HrScheduleCalendarLegend';
import { useEmployeeScheduleCalendar } from '@/modules/hr/hooks/useEmployeeScheduleCalendar';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import type { HrLeaveDaysPreview, HrLeaveRequest, HrWorkforceSettings } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

const LEAVE_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export function HrLeavesPage() {
  const pagination = usePagination();
  const currentUserId = useAppSelector((s) => s.auth.user?.userId);
  const { employees, getLabel } = useEmployeeOptions({ pageSize: 200, hrScoped: true });
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [granularity, setGranularity] = useState<HrCalendarGranularity>('month');
  const [viewDate, setViewDate] = useState(() => new Date());
  const [filters, setFilters] = useState<HrWorkforceFilters>({
    employeeId: '',
    departmentId: '',
    status: '',
    leaveTypeId: '',
    dateFrom: '',
    dateTo: '',
  });
  const [leaveTypes, setLeaveTypes] = useState<{ value: string; label: string }[]>([]);
  const [workforceSettings, setWorkforceSettings] = useState<HrWorkforceSettings | null>(null);

  const [data, setData] = useState<HrLeaveRequest[]>([]);
  const [calendarData, setCalendarData] = useState<HrLeaveRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [employeeId, setEmployeeId] = useState('');
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [durationMode, setDurationMode] = useState('FULL_DAY');
  const [halfDaySession, setHalfDaySession] = useState('MORNING');
  const [leaveHours, setLeaveHours] = useState('1');
  const [reason, setReason] = useState('');
  const [creating, setCreating] = useState(false);
  const [leavePreview, setLeavePreview] = useState<HrLeaveDaysPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [cellModal, setCellModal] = useState<{
    employeeId: number;
    dayKey: string;
    rows: HrLeaveRequest[];
  } | null>(null);
  const [modalLeaveTypeId, setModalLeaveTypeId] = useState('');
  const [modalDurationMode, setModalDurationMode] = useState('FULL_DAY');
  const [modalHalfDaySession, setModalHalfDaySession] = useState('MORNING');
  const [modalLeaveHours, setModalLeaveHours] = useState('1');
  const [modalEndDate, setModalEndDate] = useState('');
  const [modalReason, setModalReason] = useState('');
  const [modalLeavePreview, setModalLeavePreview] = useState<HrLeaveDaysPreview | null>(null);
  const [modalPreviewLoading, setModalPreviewLoading] = useState(false);

  useEffect(() => {
    hrApi.getWorkforceSettings().then(setWorkforceSettings).catch(() => setWorkforceSettings(null));
    hrApi.listLeaveTypes().then((types) =>
      setLeaveTypes(types.map((t) => ({ value: String(t.id), label: t.name }))),
    );
  }, []);

  const listParams = useMemo(() => {
    const range =
      filters.dateFrom && filters.dateTo
        ? { dateFrom: filters.dateFrom, dateTo: filters.dateTo }
        : viewMode === 'calendar'
          ? hrCalendarRangeForGranularity(viewDate, granularity)
          : {};
    return {
      page: pagination.page,
      size: pagination.size,
      employeeId: filters.employeeId ? Number(filters.employeeId) : undefined,
      departmentId: filters.departmentId ? Number(filters.departmentId) : undefined,
      leaveTypeId: filters.leaveTypeId ? Number(filters.leaveTypeId) : undefined,
      requestStatus: filters.status || undefined,
      ...range,
    };
  }, [filters, pagination.page, pagination.size, viewDate, granularity, viewMode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrApi.listLeaves(listParams);
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load leaves');
    } finally {
      setLoading(false);
    }
  }, [listParams]);

  const loadCalendar = useCallback(async () => {
    setCalendarLoading(true);
    try {
      const { start, end } =
        filters.dateFrom && filters.dateTo
          ? { start: filters.dateFrom, end: filters.dateTo }
          : hrCalendarRangeForGranularity(viewDate, granularity);
      const rows = await fetchAllHrPages((page) =>
        hrApi.listLeaves({
          page,
          size: 200,
          dateFrom: start,
          dateTo: end,
          employeeId: filters.employeeId ? Number(filters.employeeId) : undefined,
          departmentId: filters.departmentId ? Number(filters.departmentId) : undefined,
          leaveTypeId: filters.leaveTypeId ? Number(filters.leaveTypeId) : undefined,
          requestStatus: filters.status || undefined,
        }),
      );
      setCalendarData(rows);
    } catch (err) {
      showApiError(err, 'Failed to load leave calendar');
    } finally {
      setCalendarLoading(false);
    }
  }, [viewDate, granularity, filters]);

  useEffect(() => {
    if (viewMode === 'list') {
      load();
    } else {
      loadCalendar();
    }
  }, [viewMode, load, loadCalendar]);

  useEffect(() => {
    if (!employeeId || !startDate || !endDate) {
      setLeavePreview(null);
      return;
    }
    if (endDate < startDate) {
      setLeavePreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    hrApi
      .previewLeaveDays(Number(employeeId), startDate, endDate, {
        durationMode,
        halfDaySession: durationMode === 'HALF_DAY' ? halfDaySession : undefined,
        leaveHours: durationMode === 'HOURLY' ? Number(leaveHours) : undefined,
      })
      .then((preview) => {
        if (!cancelled) setLeavePreview(preview);
      })
      .catch(() => {
        if (!cancelled) setLeavePreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, startDate, endDate, durationMode, halfDaySession, leaveHours]);

  const refreshAll = () => {
    load();
    if (viewMode === 'calendar') loadCalendar();
  };

  const createLeave = async () => {
    if (!employeeId || !leaveTypeId || !startDate || !endDate) {
      notify.error('Employee, leave type, and dates are required');
      return;
    }
    if (endDate < startDate) {
      notify.error('End date cannot be before start date');
      return;
    }
    setCreating(true);
    try {
      await hrApi.createLeave({
        employeeId: Number(employeeId),
        leaveTypeId: Number(leaveTypeId),
        startDate,
        endDate,
        reason: reason.trim() || undefined,
        durationMode,
        halfDaySession: durationMode === 'HALF_DAY' ? halfDaySession : undefined,
        leaveHours: durationMode === 'HOURLY' ? Number(leaveHours) : undefined,
      });
      notify.success('Leave request created');
      setEmployeeId('');
      setReason('');
      refreshAll();
    } catch (err) {
      showApiError(err, 'Failed to create leave');
    } finally {
      setCreating(false);
    }
  };

  const createLeaveFromCalendar = async () => {
    if (!cellModal || !modalLeaveTypeId) {
      notify.error('Leave type is required');
      return;
    }
    const start = cellModal.dayKey;
    const end = modalDurationMode === 'FULL_DAY' ? modalEndDate : start;
    if (!end || end < start) {
      notify.error('End date cannot be before start date');
      return;
    }
    setCreating(true);
    try {
      await hrApi.createLeave({
        employeeId: cellModal.employeeId,
        leaveTypeId: Number(modalLeaveTypeId),
        startDate: start,
        endDate: end,
        reason: modalReason.trim() || undefined,
        durationMode: modalDurationMode,
        halfDaySession: modalDurationMode === 'HALF_DAY' ? modalHalfDaySession : undefined,
        leaveHours: modalDurationMode === 'HOURLY' ? Number(modalLeaveHours) : undefined,
      });
      notify.success('Leave request created');
      setCellModal(null);
      refreshAll();
    } catch (err) {
      showApiError(err, 'Failed to create leave');
    } finally {
      setCreating(false);
    }
  };

  const approveLeave = async (id: number) => {
    const approver = currentUserId ?? 0;
    if (!approver) {
      notify.error('Unable to resolve approver');
      return;
    }
    try {
      await hrApi.approveLeave(id, approver);
      notify.success('Leave approved');
      setCellModal(null);
      refreshAll();
    } catch (err) {
      showApiError(err, 'Leave approval failed');
    }
  };

  const rejectLeave = async (id: number) => {
    const approver = currentUserId ?? 0;
    if (!approver) return;
    try {
      await hrApi.rejectLeave(id, approver);
      notify.success('Leave rejected');
      setCellModal(null);
      refreshAll();
    } catch (err) {
      showApiError(err, 'Reject failed');
    }
  };

  const cancelLeave = async (id: number) => {
    try {
      await hrApi.cancelLeave(id);
      notify.success('Leave cancelled');
      setCellModal(null);
      refreshAll();
    } catch (err) {
      showApiError(err, 'Cancel failed');
    }
  };

  const calendarDays = useMemo(
    () => hrCalendarDaysForGranularity(viewDate, granularity),
    [viewDate, granularity],
  );
  const { start: rangeStart, end: rangeEnd } = hrCalendarRangeForGranularity(viewDate, granularity);

  const leavesForMonth = useMemo(() => {
    return calendarData.filter((row) => row.endDate >= rangeStart && row.startDate <= rangeEnd);
  }, [calendarData, rangeStart, rangeEnd]);

  const leavesByEmployeeDay = useMemo(() => {
    const map = new Map<number, Map<string, HrLeaveRequest[]>>();
    for (const row of leavesForMonth) {
      const byDay = map.get(row.employeeId) ?? new Map<string, HrLeaveRequest[]>();
      for (const dayKey of expandLeaveDays(row.startDate, row.endDate, rangeStart, rangeEnd)) {
        const list = byDay.get(dayKey) ?? [];
        list.push(row);
        byDay.set(dayKey, list);
      }
      map.set(row.employeeId, byDay);
    }
    return map;
  }, [leavesForMonth, rangeEnd, rangeStart]);

  const scheduleEmployees = useMemo((): HrScheduleEmployee[] => {
    let source = employees;
    if (filters.employeeId) {
      source = employees.filter((e) => String(e.id) === filters.employeeId);
    }
    return [...source]
      .sort((a, b) => {
        const an = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
        const bn = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
        return an.localeCompare(bn);
      })
      .map((employee) => ({
        id: employee.id,
        name: [employee.firstName, employee.lastName].filter(Boolean).join(' ') || getLabel(employee.id, true),
        subtitle: employee.employeeCode ?? undefined,
        initials: hrEmployeeInitials(employee.firstName, employee.lastName),
      }));
  }, [employees, filters.employeeId, getLabel]);

  const scheduleEmployeeIds = useMemo(() => scheduleEmployees.map((e) => e.id), [scheduleEmployees]);
  const scheduleCalendar = useEmployeeScheduleCalendar({
    viewDate,
    employeeIds: scheduleEmployeeIds,
    enabled: viewMode === 'calendar' && scheduleEmployeeIds.length > 0,
  });

  const employeeLeaveDays = useMemo(() => {
    const totals = new Map<number, number>();
    for (const row of leavesForMonth) {
      totals.set(row.employeeId, (totals.get(row.employeeId) ?? 0) + Number(row.totalDays));
    }
    return totals;
  }, [leavesForMonth]);

  const durationOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    if (workforceSettings?.fullDayEnabled ?? true) opts.push({ value: 'FULL_DAY', label: 'Full day' });
    if (workforceSettings?.halfDayEnabled ?? true) opts.push({ value: 'HALF_DAY', label: 'Half day' });
    if (workforceSettings?.hourlyEnabled) opts.push({ value: 'HOURLY', label: 'Hourly' });
    return opts.length > 0 ? opts : [{ value: 'FULL_DAY', label: 'Full day' }];
  }, [workforceSettings]);

  useEffect(() => {
    if (!cellModal) return;
    setModalLeaveTypeId('');
    setModalDurationMode(durationOptions[0]?.value ?? 'FULL_DAY');
    setModalHalfDaySession('MORNING');
    setModalLeaveHours('1');
    setModalEndDate(cellModal.dayKey);
    setModalReason('');
    setModalLeavePreview(null);
  }, [cellModal, durationOptions]);

  useEffect(() => {
    if (!cellModal || !modalLeaveTypeId) {
      setModalLeavePreview(null);
      return;
    }
    const start = cellModal.dayKey;
    const end = modalDurationMode === 'FULL_DAY' ? modalEndDate : start;
    if (!end || end < start) {
      setModalLeavePreview(null);
      return;
    }
    let cancelled = false;
    setModalPreviewLoading(true);
    hrApi
      .previewLeaveDays(cellModal.employeeId, start, end, {
        durationMode: modalDurationMode,
        halfDaySession: modalDurationMode === 'HALF_DAY' ? modalHalfDaySession : undefined,
        leaveHours: modalDurationMode === 'HOURLY' ? Number(modalLeaveHours) : undefined,
      })
      .then((preview) => {
        if (!cancelled) setModalLeavePreview(preview);
      })
      .catch(() => {
        if (!cancelled) setModalLeavePreview(null);
      })
      .finally(() => {
        if (!cancelled) setModalPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cellModal, modalLeaveTypeId, modalEndDate, modalDurationMode, modalHalfDaySession, modalLeaveHours]);

  const columns: Column<HrLeaveRequest>[] = [
    { key: 'id', header: '#' },
    { key: 'employeeId', header: 'Employee', render: (row) => getLabel(row.employeeId, true) },
    { key: 'startDate', header: 'From' },
    { key: 'endDate', header: 'To' },
    {
      key: 'totalDays',
      header: 'Days',
      render: (row) => hrFormatLeaveDays(row.totalDays),
    },
    { key: 'durationMode', header: 'Mode' },
    { key: 'statusCode', header: 'Status' },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) =>
        row.statusCode === 'PENDING' || row.statusCode === 'SUBMITTED' ? (
          <TableRowActions>
            <PageActionGate action="edit">
              <Button variant="secondary" size="sm" onClick={() => approveLeave(row.id)}>
                Approve
              </Button>
              <Button variant="secondary" size="sm" onClick={() => rejectLeave(row.id)}>
                Reject
              </Button>
            </PageActionGate>
          </TableRowActions>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Leave management"
        description="Hierarchy-scoped leave with calendar actions and configurable duration modes"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Leaves' }]}
      />

      <HrWorkforceFilterBar
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        mode="leave"
        statusOptions={LEAVE_STATUS_OPTIONS}
        leaveTypeOptions={leaveTypes}
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        {viewMode === 'calendar' && (
          <HrCalendarGranularityToggle value={granularity} onChange={setGranularity} />
        )}
      </div>

      {viewMode === 'calendar' && (
        <p className="mb-4 text-sm text-muted">
          Click any day on the calendar to view existing leave or create a new request for that employee.
        </p>
      )}

      {viewMode === 'list' && (
        <PageActionGate action="create">
          <div className="mb-6 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
            <h3 className="mb-2 text-sm font-medium">New leave request</h3>
          <FormGrid columns={4} className="items-end">
            <EmployeeSelect value={employeeId} onChange={setEmployeeId} hrScoped />
            <LeaveTypeSelect value={leaveTypeId} onChange={setLeaveTypeId} required />
            <Select
              label="Duration"
              value={durationMode}
              onChange={setDurationMode}
              options={durationOptions}
            />
            {durationMode === 'HALF_DAY' && (
              <Select
                label="Session"
                value={halfDaySession}
                onChange={setHalfDaySession}
                options={(workforceSettings?.halfDaySessions ?? ['MORNING', 'AFTERNOON']).map((s) => ({
                  value: s,
                  label: s,
                }))}
              />
            )}
            {durationMode === 'HOURLY' && (
              <Select
                label="Hours"
                value={leaveHours}
                onChange={setLeaveHours}
                options={(workforceSettings?.hourlyDurations ?? [1, 2, 3]).map((h) => ({
                  value: String(h),
                  label: `${h} hour${h === 1 ? '' : 's'}`,
                }))}
              />
            )}
            {durationMode === 'FULL_DAY' ? (
              <DateRangeFields
                from={startDate}
                to={endDate}
                onFromChange={setStartDate}
                onToChange={setEndDate}
                fromLabel="Start"
                toLabel="End"
              />
            ) : (
              <DatePicker label="Date" value={startDate} onChange={(v) => { setStartDate(v); setEndDate(v); }} />
            )}
            <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </FormGrid>
          <FormActions
            primaryLabel="Create leave"
            primaryLoading={creating}
            onPrimary={() => void createLeave()}
            onReset={() => {
              setEmployeeId('');
              setLeaveTypeId('');
              setDurationMode('FULL_DAY');
              setStartDate('');
              setEndDate('');
              setReason('');
              setLeavePreview(null);
            }}
          />
          {previewLoading ? (
            <p className="mt-2 text-sm text-muted">Calculating leave impact…</p>
          ) : leavePreview ? (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Scheduled leave: <strong>{hrFormatDaysCount(leavePreview.workingDays)}</strong>
            </p>
          ) : null}
          </div>
        </PageActionGate>
      )}

      {viewMode === 'list' ? (
        <DataTable columns={columns} data={data} loading={loading} keyField="id" {...dataTablePageProps(pagination)} />
      ) : (
        <div className="card p-4">
          <HrEmployeeScheduleGrid
            viewDate={viewDate}
            onViewDateChange={setViewDate}
            days={calendarDays}
            employees={scheduleEmployees}
            loading={calendarLoading || scheduleCalendar.loading}
            getEmployeeTotal={(empId) => employeeLeaveDays.get(empId) ?? null}
            formatEmployeeTotal={(_empId, total) => hrFormatDaysCount(total)}
            totalColumnLabel="Total days"
            getCellBackgroundClass={scheduleCalendar.getCellBackgroundClass}
            getHeaderBackgroundClass={scheduleCalendar.getHeaderBackgroundClass}
            getCellTitle={scheduleCalendar.getDayTooltip}
            calendarLegend={<HrScheduleCalendarLegend />}
            emptyMessage="No employees in your visibility scope."
            onCellClick={(empId, day) => {
              const dayKey = hrDateKey(day);
              const items = leavesByEmployeeDay.get(empId)?.get(dayKey) ?? [];
              setCellModal({ employeeId: empId, dayKey, rows: items });
            }}
            renderCell={(empId, _day, dayKey) => {
              const items = leavesByEmployeeDay.get(empId)?.get(dayKey) ?? [];
              if (items.length === 0) return null;
              return (
                <div className="flex flex-col gap-0.5">
                  {items.map((row) => {
                    const tip = `${row.statusCode} · ${row.durationMode ?? 'FULL_DAY'} · ${hrFormatLeaveDays(row.totalDays)}`;
                    return (
                      <Tooltip key={`${row.id}-${dayKey}`} content={tip}>
                        <div
                          className={cn(
                            'rounded border px-1 py-0.5 text-[10px] font-medium leading-tight',
                            HR_LEAVE_STYLES[row.statusCode] ?? HR_LEAVE_STYLES.PENDING,
                          )}
                        >
                          {row.statusCode === 'APPROVED' ? 'Leave' : row.statusCode}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            }}
          />
        </div>
      )}

      <Modal
        open={cellModal != null}
        onClose={() => setCellModal(null)}
        title={cellModal ? `Leave · ${getLabel(cellModal.employeeId, true)} · ${cellModal.dayKey}` : ''}
      >
        {cellModal && (
          <div className="space-y-4">
            {cellModal.rows.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200">On this day</h4>
                {cellModal.rows.map((row) => (
                  <div key={row.id} className="rounded border border-slate-200 p-3 text-sm dark:border-slate-700">
                    <div>Status: <strong>{row.statusCode}</strong></div>
                    <div>Range: {row.startDate} → {row.endDate}</div>
                    <div>Mode: {row.durationMode ?? 'FULL_DAY'}</div>
                    <div>Days: {hrFormatLeaveDays(row.totalDays)}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(row.statusCode === 'PENDING' || row.statusCode === 'SUBMITTED') && (
                        <PageActionGate action="edit">
                          <Button size="sm" onClick={() => approveLeave(row.id)}>Approve</Button>
                          <Button size="sm" variant="secondary" onClick={() => rejectLeave(row.id)}>Reject</Button>
                        </PageActionGate>
                      )}
                      {row.statusCode !== 'APPROVED' && row.statusCode !== 'CANCELLED' && (
                        <Button size="sm" variant="secondary" onClick={() => cancelLeave(row.id)}>Cancel</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <PageActionGate action="create">
              <div className="space-y-3 rounded-lg border border-dashed border-slate-300 p-3 dark:border-slate-600">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {cellModal.rows.length === 0 ? 'Request leave' : 'Add leave request'}
                </h4>
                <FormGrid columns={2} className="items-end">
                  <LeaveTypeSelect value={modalLeaveTypeId} onChange={setModalLeaveTypeId} required />
                  <Select
                    label="Duration"
                    value={modalDurationMode}
                    onChange={setModalDurationMode}
                    options={durationOptions}
                  />
                  {modalDurationMode === 'HALF_DAY' && (
                    <Select
                      label="Session"
                      value={modalHalfDaySession}
                      onChange={setModalHalfDaySession}
                      options={(workforceSettings?.halfDaySessions ?? ['MORNING', 'AFTERNOON']).map((s) => ({
                        value: s,
                        label: s,
                      }))}
                    />
                  )}
                  {modalDurationMode === 'HOURLY' && (
                    <Select
                      label="Hours"
                      value={modalLeaveHours}
                      onChange={setModalLeaveHours}
                      options={(workforceSettings?.hourlyDurations ?? [1, 2, 3]).map((h) => ({
                        value: String(h),
                        label: `${h} hour${h === 1 ? '' : 's'}`,
                      }))}
                    />
                  )}
                  {modalDurationMode === 'FULL_DAY' && (
                    <DatePicker
                      label="End date"
                      value={modalEndDate}
                      onChange={setModalEndDate}
                      min={cellModal.dayKey}
                    />
                  )}
                  <Input label="Reason" value={modalReason} onChange={(e) => setModalReason(e.target.value)} />
                  <Button loading={creating} onClick={() => void createLeaveFromCalendar()}>
                    Create leave
                  </Button>
                </FormGrid>
                {modalPreviewLoading ? (
                  <p className="text-sm text-muted">Calculating leave impact…</p>
                ) : modalLeavePreview ? (
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Scheduled leave: <strong>{hrFormatDaysCount(modalLeavePreview.workingDays)}</strong>
                  </p>
                ) : null}
              </div>
            </PageActionGate>
          </div>
        )}
      </Modal>
    </div>
  );
}

function expandLeaveDays(startDate: string, endDate: string, monthStart: string, monthEnd: string): string[] {
  const start = startDate.slice(0, 10);
  const end = endDate.slice(0, 10);
  const from = start > monthStart ? start : monthStart;
  const to = end < monthEnd ? end : monthEnd;
  const days: string[] = [];
  let cursor = from;
  while (cursor <= to) {
    if (hrLeaveSpansDate(start, end, cursor)) {
      days.push(cursor);
    }
    const next = nextDateKey(cursor);
    if (next <= cursor) break;
    cursor = next;
  }
  return days;
}

function nextDateKey(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const dt = new Date(y, m - 1, d + 1);
  return hrDateKey(dt);
}
